import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MsConsentPort } from '../../../domain/commerce-registry/ports/ms-consent.port';
import type {
  AcceptUserTermsPayload,
  AcceptUserTermsResult,
  CommerceActiveTerms,
} from '../../../domain/commerce-registry/models/ms-consent.model';
import { ENVIRONMENT } from '../../../../environments/environment';

interface ApiDataResponse<T> {
  status_code?: number;
  data?: T;
}

interface CommerceComplianceApiModel {
  id_terminos_condiciones?: number;
}

interface TermsConditionsApiModel {
  id?: number;
  version?: string | null;
  content_hash?: string | null;
  public_url?: string | null;
  pdf_url?: string | null;
  title?: string | null;
}

const CONSENT_FAIL_MESSAGE =
  'No pudimos registrar tu aceptación de términos. Intenta de nuevo en unos minutos.';

@Injectable()
export class MsConsentApiAdapter extends MsConsentPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  getCurrentComplianceTermsId(commerceId: number): Observable<number | null> {
    const base = this.baseUrl();
    if (!base) {
      return of(null);
    }
    const url = `${base}/comercios/${commerceId}/cumplimiento-tyc`;
    return this.http.get<ApiDataResponse<CommerceComplianceApiModel>>(url).pipe(
      map((res) => {
        const id = res?.data?.id_terminos_condiciones;
        return typeof id === 'number' && id > 0 ? id : null;
      }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null);
        }
        return of(null);
      }),
    );
  }

  getTermsById(termsConditionsId: number): Observable<CommerceActiveTerms | null> {
    const base = this.baseUrl();
    if (!base) {
      return of(null);
    }
    const url = `${base}/terminos-condiciones/${termsConditionsId}`;
    return this.http.get<ApiDataResponse<TermsConditionsApiModel>>(url).pipe(
      map((res) => mapTermsApiToActive(res?.data, termsConditionsId)),
      catchError(() => of(null)),
    );
  }

  acceptUserTerms(payload: AcceptUserTermsPayload): Observable<AcceptUserTermsResult> {
    const base = this.baseUrl();
    if (!base) {
      return of({ outcome: 'error', message: 'Servicio de consentimiento no configurado.' });
    }

    const url = `${base}/usuarios/terms-conditions`;
    const userAgent = payload.userAgent?.trim();
    const body = {
      uid: payload.uid,
      terms_conditions_id: payload.termsConditionsId,
      id_comercio: payload.commerceId,
      terms_hash: payload.termsHash,
      status: 'accepted',
      channel_origin: payload.channelOrigin,
      gdpr_basis: 'consentimiento_explicito',
      processing_purpose: 'marketing',
      ...(userAgent ? { user_agent: userAgent } : {}),
    };

    return this.http.post<unknown>(url, body).pipe(
      map(() => ({ outcome: 'success' as const, message: 'Consentimiento registrado.' })),
      catchError(() => of({ outcome: 'error' as const, message: CONSENT_FAIL_MESSAGE })),
    );
  }

  private baseUrl(): string {
    return ENVIRONMENT.msConsentApiUrl?.trim().replace(/\/$/, '') ?? '';
  }
}

function mapTermsApiToActive(
  data: TermsConditionsApiModel | undefined,
  fallbackId: number,
): CommerceActiveTerms | null {
  if (!data) return null;
  const hash = typeof data.content_hash === 'string' ? data.content_hash.trim() : '';
  if (!hash) return null;

  const id =
    typeof data.id === 'number' && data.id > 0 ? data.id : fallbackId;
  const version = typeof data.version === 'string' ? data.version.trim() : '';
  const publicUrl =
    typeof data.public_url === 'string' && data.public_url.trim()
      ? data.public_url.trim()
      : undefined;
  const pdfUrl =
    typeof data.pdf_url === 'string' && data.pdf_url.trim() ? data.pdf_url.trim() : undefined;
  const title =
    typeof data.title === 'string' && data.title.trim() ? data.title.trim() : undefined;

  return {
    termsConditionsId: id,
    version,
    contentHash: hash,
    ...(publicUrl ? { publicUrl } : {}),
    ...(pdfUrl ? { pdfUrl } : {}),
    ...(title ? { title } : {}),
  };
}
